import { useCallback, useMemo } from 'react';

import {
  ENRICH_CACHE_TTL_MS,
  buildEnrichCacheKey,
  invalidateEnrichCache,
  readEnrichCache,
  writeEnrichCache,
  type CachedEnrichEntry,
} from '../services/enrichResultCache';

/**
 * React binding for the enrichment result cache, scoped to one entity family.
 *
 * The point of the hook (over calling the service directly) is that a tool
 * cannot accidentally key its entries into another tool's namespace, and that
 * the three operations a dialog needs — look up, store, forget-and-refetch —
 * arrive as stable callbacks it can put in dependency arrays.
 *
 * Deliberately thin and entity-agnostic: `BulkAIEnrich` uses it today, and the
 * calibre / winder / module enrichment dialogs can adopt it by passing their own
 * `kind` and their own inputs object.
 *
 * @param kind Entity family, e.g. 'reference'. Namespaces every key.
 */
export function useEnrichResultCache<T>(kind: string) {
  /**
   * The cached result for this entity + inputs, or null.
   *
   * `inputs` must cover everything that changes the answer AND be cheap to
   * compute — see `buildEnrichCacheKey`. Passing scraped page text or fetched
   * image bytes would mean a hit still paid for the work it exists to skip.
   */
  const getCached = useCallback(
    (id: string, inputs: unknown): CachedEnrichEntry<T> | null =>
      readEnrichCache<T>(buildEnrichCacheKey({ kind, id, inputs })),
    [kind],
  );

  const putCached = useCallback(
    (id: string, inputs: unknown, value: T): void => {
      writeEnrichCache<T>(buildEnrichCacheKey({ kind, id, inputs }), value);
    },
    [kind],
  );

  /** Forget this entity's entry so the next call goes to the API. */
  const invalidate = useCallback(
    (id: string, inputs: unknown): void => {
      invalidateEnrichCache(buildEnrichCacheKey({ kind, id, inputs }));
    },
    [kind],
  );

  return useMemo(
    () => ({ getCached, putCached, invalidate, ttlMs: ENRICH_CACHE_TTL_MS }),
    [getCached, putCached, invalidate],
  );
}
